import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2 } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { SectionHead } from "@/components/site/SectionHead";
import { CtaBand } from "@/components/site/CtaBand";
import { BlogLinks } from "@/components/site/BlogLinks";
import { ServiceIcon } from "@/components/site/ServiceIcon";
import { SparkleText } from "@/components/site/Sparkle";
import { CaseCard } from "@/components/site/CaseCard";
import { FaqList } from "@/components/site/FaqList";
import { useContent } from "@/lib/api";
import { Seo, breadcrumbLd, faqLd, PROVIDER } from "@/components/site/Seo";
import { SITE_URL } from "@/lib/api";
import { INFO_PAGES } from "@/data/infoPages";

const Paras = ({ items, className = "" }) => items.map((p) => <p key={p} className={className}>{p}</p>);

const EnquireBtn = ({ label, testid, to = "/contact", className = "btn-accent" }) => (
  <Link to={to} className={className} data-testid={testid}>{label} <ArrowUpRight className="h-4 w-4" /></Link>
);

const Cases = ({ channels }) => {
  const { items } = useContent("case_studies");
  const cases = (Array.isArray(items) ? items : []).filter((c) => (c.channels || []).some((ch) => channels.includes(ch))).slice(0, 3);
  if (!cases.length) return null;
  return (
    <section className="bg-[#F5F0FF] py-20 sm:py-28" data-testid="info-cases">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <SectionHead eyebrow="Results" title="Recent Client Results" />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {cases.map((c, i) => <Reveal key={c.slug} delay={i * 0.06} className="h-full"><CaseCard c={c} i={i} /></Reveal>)}
        </div>
        <Link to="/results" className="btn-ghost mt-8" data-testid="info-results-link">See all results & case studies <ArrowUpRight className="h-4 w-4" /></Link>
      </div>
    </section>
  );
};

const Faqs = ({ title, faqs }) => (
  <section className="bg-[#F5F0FF] py-20 sm:py-28" data-testid="info-faqs">
    <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[1fr_1.4fr]">
      <SectionHead eyebrow="FAQs" title={`${title}: your questions answered`} />
      <Reveal><FaqList faqs={faqs} /></Reveal>
    </div>
  </section>
);

const Ticks = ({ items, dark }) => (
  <ul className="mt-4 space-y-2.5">
    {items.map((t) => (
      <li key={t} className={`flex items-start gap-2.5 text-sm leading-snug ${dark ? "text-violet-50" : "text-[#13052A]"}`}>
        <CheckCircle2 className={`mt-0.5 h-4 w-4 shrink-0 ${dark ? "text-[#60A5FA]" : "text-[#7C3AED]"}`} />{t}
      </li>
    ))}
  </ul>
);

const Card = ({ c }) => (
  <div className="h-full rounded-[24px] bg-white p-7">
    <h3 className="text-lg font-extrabold text-[#13052A]">{c.title}</h3>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600"><Paras items={c.paras} /></div>
    {c.list && <Ticks items={c.list} />}
    {c.after && <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600"><Paras items={c.after} /></div>}
  </div>
);

const What = ({ d }) => (
  <section className="bg-white py-20 sm:py-28" data-testid="info-what">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.2fr]">
      <SectionHead eyebrow="The basics" title={d.what.title} />
      <Reveal className="space-y-4 text-base leading-relaxed text-slate-600 md:text-lg">
        <Paras items={d.what.paras} />
      </Reveal>
    </div>
  </section>
);

const Services = ({ s }) => (
  <section className="bg-[#F5F0FF] py-20 sm:py-28" data-testid="info-services">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead eyebrow="What's included" title={s.title} />
      {s.intro && <Reveal className="mt-6 max-w-3xl space-y-2 text-base leading-relaxed text-slate-600 md:text-lg"><Paras items={s.intro} /></Reveal>}
      {s.list && (
        <Reveal className="mt-10 rounded-[28px] bg-white p-7 sm:p-10">
          <ul className="grid gap-x-8 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {s.list.map((c) => <li key={c} className="flex items-start gap-3 text-base font-semibold text-[#13052A]"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#7C3AED]" />{c}</li>)}
          </ul>
        </Reveal>
      )}
      {s.cards && (
        <div className={`mt-10 grid gap-4 sm:grid-cols-2 ${s.wide ? "" : "lg:grid-cols-3"}`}>
          {s.cards.map((c, i) => (
            <Reveal key={c.title} delay={(i % 3) * 0.06} className="h-full"><Card c={c} /></Reveal>
          ))}
        </div>
      )}
      {s.note && <Reveal><p className="mt-6 text-sm text-slate-600">{s.note}</p></Reveal>}
    </div>
  </section>
);

const Why = ({ w }) => (
  <section className="grain relative overflow-hidden bg-[#13052A] py-20 text-white sm:py-28" data-testid="info-why">
    <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
      <SectionHead dark eyebrow="Why it matters" title={w.title} />
      <div className="mt-12 grid gap-8 md:grid-cols-2">
        {w.cards.map((c, i) => (
          <Reveal key={c.title} delay={(i % 2) * 0.08}>
            <div className="border-t border-white/15 pt-6">
              <span className="serif-accent text-4xl text-[#C4B5FD]">{i + 1}</span>
              <h3 className="mt-2 text-xl font-extrabold">{c.title}</h3>
              <div className="mt-3 space-y-3 text-base leading-relaxed text-violet-100"><Paras items={c.paras} /></div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Approach = ({ a }) => (
  <section className="bg-white py-20 sm:py-28" data-testid="info-approach">
    <div className="mx-auto grid max-w-7xl gap-10 px-5 sm:px-8 lg:grid-cols-[1fr_1.1fr]">
      <div>
        <SectionHead eyebrow="Our approach" title={a.title} />
        <Reveal className="mt-6 space-y-4 text-base leading-relaxed text-slate-600 md:text-lg"><Paras items={a.paras} /></Reveal>
      </div>
      <Reveal delay={0.08} className="rounded-[28px] bg-[#F5F0FF] p-8 sm:p-10">
        <p className="text-base font-bold text-[#13052A]">{a.lead}</p>
        <Ticks items={a.list} />
        <p className="mt-6 border-t border-violet-100 pt-6 text-base leading-relaxed text-slate-600">{a.after}</p>
      </Reveal>
    </div>
  </section>
);

const Related = ({ links }) => (
  <section className="bg-white py-20 sm:py-24" data-testid="info-related">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <p className="eyebrow text-[#7C3AED]"><span className="h-1.5 w-1.5 rounded-full bg-[#2563EB]" />Related services</p>
      <div className="mt-5 flex flex-wrap gap-3">
        {links.map(([to, label]) => (
          <Link key={to} to={to} className="btn-ghost" data-testid={`info-related-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{label} <ArrowUpRight className="h-4 w-4" /></Link>
        ))}
      </div>
    </div>
  </section>
);

const WhoChoose = ({ d }) => (
  <section className="bg-white py-20 sm:py-28">
    <div className="mx-auto grid max-w-7xl gap-6 px-5 sm:px-8 lg:grid-cols-2">
      <Reveal className="rounded-[28px] bg-[#13052A] p-8 text-white sm:p-10" data-testid="info-who">
        <h2 className="text-2xl font-extrabold leading-tight sm:text-3xl">{d.who.title}</h2>
        <p className="mt-5 text-base font-bold">{d.who.lead}</p>
        <ul className="mt-5 space-y-4">
          {d.who.items.map((it) => (
            <li key={it.text} className="flex items-start gap-3 text-base text-violet-50">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#60A5FA]" />
              <span>{it.label && <strong className="text-white">{it.label}: </strong>}{it.text}</span>
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.08} className="rounded-[28px] bg-[#F5F0FF] p-8 sm:p-10" data-testid="info-choose">
        <p className="eyebrow text-[#7C3AED]">Why Choose Digital AdDoctor?</p>
        <h2 className="serif-accent mt-4 text-3xl leading-tight text-[#581C87] sm:text-4xl" data-testid="info-choose-tagline"><SparkleText text={d.choose.tagline} /></h2>
        <div className="mt-5 space-y-4 text-base leading-relaxed text-slate-700"><Paras items={d.choose.paras} /></div>
      </Reveal>
    </div>
  </section>
);

const Pricing = ({ d }) => (
  <section className="bg-white pb-20 sm:pb-28" data-testid="info-pricing">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="flex flex-col gap-6 rounded-[28px] border border-violet-200 p-8 sm:p-10 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-2xl">
          <p className="eyebrow text-[#7C3AED]">{d.pricing.title}</p>
          <h2 className="mt-4 text-3xl font-extrabold text-[#13052A] sm:text-4xl" data-testid="info-price">{d.pricing.price}</h2>
          <div className="mt-4 space-y-2 text-base leading-relaxed text-slate-600"><Paras items={d.pricing.paras} /></div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-3">
          <EnquireBtn label={d.heroCta} to={d.heroCtaTo} testid="info-pricing-cta" />
          <Link to="/pricing" className="btn-ghost" data-testid="info-all-pricing">View all pricing</Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function InfoPage({ slug }) {
  const d = INFO_PAGES[slug];
  const crumbs = [["Home", "/"], [d.navTitle, `/${slug}`]];
  return (
    <>
      <Seo title={d.metaTitle} description={d.metaDescription} path={`/${slug}`}
        jsonLd={[{ "@type": "Service", name: d.navTitle, serviceType: d.navTitle, description: d.metaDescription, provider: PROVIDER, areaServed: ["Tunbridge Wells", "Kent", "United Kingdom"], url: `${SITE_URL}/${slug}` }, breadcrumbLd(crumbs), faqLd(d.faqs || [])]} />
      <PageHero crumbs={crumbs} eyebrow={d.eyebrow} lines={[d.h1]} intro={<SparkleText text={d.tagline} />}
        aside={
          <div className="rounded-[28px] border border-white/10 bg-white/5 p-7 backdrop-blur-xl" data-testid="info-hero-aside">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#7C3AED] text-white"><ServiceIcon name={d.icon} className="h-6 w-6" /></span>
            <div className="mt-6 space-y-3 text-sm leading-relaxed text-violet-100"><Paras items={d.intro} /></div>
            <p className="mt-5 text-sm text-violet-200">{d.heroPrice.label}: <span className="font-bold text-white">{d.heroPrice.value}</span></p>
          </div>
        }>
        <EnquireBtn label={d.heroCta} to={d.heroCtaTo} testid="info-hero-cta" />
      </PageHero>
      <What d={d} />
      {d.whyFirst && <Why w={d.why} />}
      <Services s={d.services} />
      {!d.whyFirst && <Why w={d.why} />}
      {d.approach && <Approach a={d.approach} />}
      <WhoChoose d={d} />
      <Pricing d={d} />
      {d.cases && <Cases channels={d.cases} />}
      {d.faqs && <Faqs title={d.navTitle} faqs={d.faqs} />}
      <BlogLinks page={slug} />
      {d.related && <Related links={d.related} />}
      <CtaBand title={d.final.title} text={<>{d.final.paras.map((p) => <span key={p} className="mt-2 block">{p}</span>)}</>}>
        <EnquireBtn label={d.final.cta || "Get in Touch"} to={d.final.ctaTo} testid="info-final-cta" />
      </CtaBand>
    </>
  );
}
